import { type Dir, fmtHHMM, fmtPct, fmtRiders, stationLabel } from "../demand/demandMath.ts";
import type { SimResult, SimTotals, StopDirResult } from "./types.ts";

/**
 * What the scorecard says about a simulated day, kept free of React so the
 * wording and the arithmetic can be tested (scorecardMath.test.ts).
 */

/** Which way a change in a metric is good. */
export type Better = "lower" | "higher" | "neither";

export interface Metric {
  key: keyof SimTotals;
  label: string;
  /** A second line under the label, in the table's muted ink. */
  note?: string;
  better: Better;
  format: (v: number) => string;
  /** Changes smaller than this are "about the same". */
  tolerance: number;
}

const minutes = (v: number) => (v < 10 ? `${v.toFixed(1)} min` : `${Math.round(v)} min`);
const hours = (v: number) => `${v < 10 ? v.toFixed(1) : Math.round(v)} h`;
const count = (v: number) => fmtRiders(v);

/** The scorecard's rows, in the order a planner reads them: riders first, cost last. */
export const METRICS: Metric[] = [
  { key: "boarded", label: "Riders carried", note: "of the riders who came to a stop", better: "higher", format: count, tolerance: 0.5 },
  { key: "leftBehind", label: "Left behind by a full bus", note: "riders, counted once however many buses passed them", better: "lower", format: count, tolerance: 0.5 },
  { key: "unserved", label: "No bus came", note: "gave up waiting, arrived after the last trip, or still waiting at the end", better: "lower", format: count, tolerance: 0.5 },
  { key: "gaveUp", label: "Of them, gave up waiting", note: "left after the patience set above", better: "lower", format: count, tolerance: 0.5 },
  { key: "waitMeanMin", label: "Average wait", better: "lower", format: minutes, tolerance: 0.05 },
  { key: "waitMaxMin", label: "Longest wait", better: "lower", format: minutes, tolerance: 0.5 },
  { key: "waitOver10Share", label: "Waited over ten minutes", note: "share of riders who boarded", better: "lower", format: fmtPct, tolerance: 0.005 },
  { key: "tripsFull", label: "Trips that left riders behind", better: "lower", format: count, tolerance: 0.5 },
  { key: "tripsEmpty", label: "Trips running nearly empty", note: "never a tenth full", better: "lower", format: count, tolerance: 0.5 },
  { key: "buses", label: "Buses needed", better: "lower", format: count, tolerance: 0.5 },
  { key: "busHours", label: "Bus-hours in service", better: "lower", format: hours, tolerance: 0.05 },
  { key: "ridersPerBusHour", label: "Riders per bus-hour", better: "higher", format: (v) => v.toFixed(0), tolerance: 0.5 },
  { key: "utilisation", label: "Room on the road used", note: "rider-links carried over capacity-links run", better: "neither", format: fmtPct, tolerance: 0.005 },
];

export type Verdict = "better" | "worse" | "same" | "changed";

/** How the draft's figure stands against the active timetable's. */
export function verdict(metric: Metric, active: number, draft: number): Verdict {
  const delta = draft - active;
  if (Math.abs(delta) <= metric.tolerance) return "same";
  if (metric.better === "neither") return "changed";
  const improved = metric.better === "lower" ? delta < 0 : delta > 0;
  return improved ? "better" : "worse";
}

/** The change as text: "−6,210", "+2", "−7.4 min". */
export function fmtDelta(metric: Metric, active: number, draft: number): string {
  const delta = draft - active;
  if (Math.abs(delta) <= metric.tolerance) return "–";
  const sign = delta < 0 ? "−" : "+";
  return `${sign}${metric.format(Math.abs(delta))}`;
}

export interface Narrative {
  /** One or two sentences a planner can read out. */
  text: string;
  /** The stop and direction most riders were left behind at, when any were. */
  worst: { stop: number; dir: Dir; from: number; to: number; riders: number } | null;
}

/** The stop and direction where the most riders were left behind. */
export function worstStopDir(result: SimResult): StopDirResult | null {
  let worst: StopDirResult | null = null;
  for (const sd of result.stopDirs) {
    if (sd.leftBehind >= 0.5 && (!worst || sd.leftBehind > worst.leftBehind)) worst = sd;
  }
  return worst;
}

/** The stop and direction where a rider waited longest. */
export function longestWait(result: SimResult): StopDirResult | null {
  let best: StopDirResult | null = null;
  for (const sd of result.stopDirs) {
    if (sd.boarded > 0.5 && (!best || sd.waitMaxMin > best.waitMaxMin)) best = sd;
  }
  return best;
}

/**
 * What happened, in words. The figures are the day's totals; the place and
 * the hours come from where riders were actually left standing.
 */
export function describe(result: SimResult, dirNames: Record<Dir, string>): Narrative {
  const t = result.totals;
  const stops = result.input.stops;
  const name = (i: number) => stationLabel(stops[i]?.name ?? `stop ${i + 1}`);

  if (t.trips === 0) {
    return { text: "No trips run, so nobody travels.", worst: null };
  }

  const parts: string[] = [];
  const worst = worstStopDir(result);
  let worstOut: Narrative["worst"] = null;

  if (worst) {
    const at = result.events.filter((e) => e.stop === worst.stop && e.dir === worst.dir);
    const from = at.length ? at[0].timeMin : worst.maxWaitingAt;
    const to = at.length ? at[at.length - 1].timeMin : worst.maxWaitingAt;
    worstOut = { stop: worst.stop, dir: worst.dir, from, to, riders: worst.leftBehind };
    const share = t.leftBehind > 0 ? worst.leftBehind / t.leftBehind : 0;
    const where = `${name(worst.stop)} ${dirNames[worst.dir]}`;
    const when = to - from >= 5 ? ` between ${fmtHHMM(from)} and ${fmtHHMM(to)}` : ` around ${fmtHHMM(from)}`;
    parts.push(
      `${fmtRiders(t.leftBehind)} riders a day are left behind by a full bus` +
        (share >= 0.3 ? `, ${fmtPct(share)} of them at ${where}${when}` : `, most at ${where}${when}`) + "."
    );
  } else {
    parts.push("Every rider gets on the first bus going their way.");
  }

  if (t.unserved >= 1) {
    parts.push(
      t.gaveUp >= 1
        ? `${fmtRiders(t.unserved)} a day never get a bus: ${fmtRiders(t.gaveUp)} give up waiting and the rest arrive after the last trip.`
        : `${fmtRiders(t.unserved)} a day arrive after the last bus, or are still waiting when the day ends.`
    );
  }

  const lw = longestWait(result);
  if (lw && t.boarded > 0) {
    parts.push(
      `Riders wait ${minutes(t.waitMeanMin)} on average; the longest wait is ${minutes(t.waitMaxMin)}, at ${name(lw.stop)} ${dirNames[lw.dir]}.`
    );
  }

  return { text: parts.join(" "), worst: worstOut };
}

/** A compact summary line for the generator's strip: the draft, then the active day for comparison. */
export function stripSummary(draft: SimTotals, active: SimTotals | null): string {
  const line = (t: SimTotals) =>
    `${count(t.leftBehind)} riders left behind, ${minutes(t.waitMeanMin)} average wait, ` +
    `${count(t.buses)} bus${t.buses === 1 ? "" : "es"} for ${hours(t.busHours)}`;
  return `Simulated: ${line(draft)}.` + (active ? ` Active timetable: ${line(active)}.` : "");
}
